import { Fragment, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Nav — editorial sidebar (paper-2 bg, mono-numbered items, teal active-border).
 *
 * Pure presentational. Groups come from the `groups` prop; the active item is
 * keyed off `activeId`; clicking an item calls `onNavigate(item.id)`. Collapse
 * state is local (number-only mini-strip when collapsed). No router, no stores.
 *
 *   - Expanded 260px: full numbered + labeled item list grouped by section
 *   - Collapsed 56px: number-only mini-strip with group separators
 *   - Toggle in top-right (◀ collapse / ▶ expand)
 */

export interface NavItem {
  id: string;
  num: string;
  name: string;
  meta?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface NavProps {
  groups: NavGroup[];
  activeId: string;
  onNavigate: (id: string) => void;
}

interface StripItem extends NavItem {
  groupIndex: number;
  itemIndex: number;
}

export function Nav({ groups, activeId, onNavigate }: NavProps) {
  const [collapsed, setCollapsed] = useState(false);

  // Flat strip for collapsed view — numbered items with separators between groups.
  const stripItems: StripItem[] = groups.flatMap((g, gi) =>
    g.items.map((it, ii) => ({ ...it, groupIndex: gi, itemIndex: ii })),
  );

  return (
    <nav className={cn(collapsed && 'collapsed')} aria-label="primary navigation">
      <button
        type="button"
        className="nav-toggle"
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? '▶' : '◀'}
      </button>

      {/* Expanded: full grouped list */}
      <div className="nav-body side-pad">
        {groups.map((g) => (
          <div key={g.label} className="side-group">
            <div className="side-label">{g.label}</div>
            {g.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                className={cn('side-item', item.id === activeId && 'active')}
                style={{ width: '100%', textAlign: 'left', textDecoration: 'none' }}
              >
                <span className="num">{item.num}</span>
                <span className="name">{item.name}</span>
                <span className="meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {item.meta && <span>{item.meta}</span>}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Collapsed: number-only mini-strip with group separators */}
      <div className="nav-strip">
        {stripItems.map((it, i) => {
          const isActive = it.id === activeId;
          const prev = i > 0 ? stripItems[i - 1] : undefined;
          const showSep = prev !== undefined && it.groupIndex !== prev.groupIndex;
          return (
            <Fragment key={it.id}>
              {showSep && <div className="nav-strip-sep" />}
              <button
                type="button"
                onClick={() => onNavigate(it.id)}
                className={cn('nav-strip-item', isActive && 'active')}
                title={`${it.num} · ${it.name}`}
                style={{ textDecoration: 'none' }}
              >
                <span className="num">{it.num}</span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
}
